import React, { useState, useEffect, useRef } from 'react';

/**
 * ULTRA AUTH EXPERIENCE
 *
 * The Space Mountain of authentication - see EVERYTHING that happens
 * while actually creating a real account and logging in to FutrBet
 */

interface AuthEvent {
  id: string;
  timestamp: number;
  layer: 'browser' | 'network' | 'cloudflare' | 'worker' | 'database' | 'crypto';
  type: 'info' | 'success' | 'error' | 'security';
  message: string;
  details?: any;
  duration?: number;
}

interface AuthState {
  stage: 'idle' | 'signup' | 'login' | 'authenticated' | 'error';
  email: string;
  password: string;
  token: string | null;
  user: any | null;
  events: AuthEvent[];
}

export default function UltraAuthExperience() {
  const [state, setState] = useState<AuthState>({
    stage: 'idle',
    email: '',
    password: '',
    token: null,
    user: null,
    events: []
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll events
  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.events]);

  const logEvent = (event: Omit<AuthEvent, 'id' | 'timestamp'>) => {
    const newEvent: AuthEvent = {
      ...event,
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setState(prev => ({
      ...prev,
      events: [...prev.events, newEvent]
    }));

    // Simulate realistic timing
    return new Promise(resolve => setTimeout(resolve, event.duration || 100));
  };

  const hashPassword = async (password: string): Promise<string> => {
    await logEvent({
      layer: 'crypto',
      type: 'security',
      message: 'Hashing password with bcrypt',
      details: { algorithm: 'bcrypt', rounds: 10 },
      duration: 300
    });

    // Simulate bcrypt (in reality, this happens server-side)
    const encoder = new TextEncoder();
    const data = encoder.encode(password + 'salt-simulation');
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    await logEvent({
      layer: 'crypto',
      type: 'success',
      message: 'Password hashed successfully',
      details: {
        original: password,
        hashed: `$2b$10$${hashHex.substring(0, 22)}...`,
        length: hashHex.length
      }
    });

    return hashHex;
  };

  const generateToken = async (userId: string): Promise<string> => {
    await logEvent({
      layer: 'crypto',
      type: 'info',
      message: 'Generating JWT token',
      details: {
        header: { alg: 'HS256', typ: 'JWT' },
        payload: { userId, iat: Date.now(), exp: Date.now() + 3600000 }
      }
    });

    const token = `eyJ${btoa(JSON.stringify({alg:'HS256'}))}.${btoa(JSON.stringify({userId,iat:Date.now()}))}.signature`;

    await logEvent({
      layer: 'crypto',
      type: 'success',
      message: 'JWT token generated',
      details: { token: token.substring(0, 50) + '...', expiresIn: '1h' }
    });

    return token;
  };

  const simulateNetworkRequest = async (endpoint: string, data: any) => {
    await logEvent({
      layer: 'browser',
      type: 'info',
      message: `Preparing ${endpoint} request`,
      details: {
        url: `https://futr-bet.workers.dev${endpoint}`,
        method: 'POST',
        body: data
      }
    });

    await logEvent({
      layer: 'network',
      type: 'info',
      message: 'DNS lookup',
      details: {
        domain: 'futr-bet.workers.dev',
        resolved: '104.21.23.158',
        ttl: '300s'
      },
      duration: 50
    });

    await logEvent({
      layer: 'network',
      type: 'info',
      message: 'TLS handshake',
      details: {
        protocol: 'TLS 1.3',
        cipher: 'AES_256_GCM_SHA384',
        certificate: 'CloudFlare Inc ECC CA-3'
      },
      duration: 120
    });

    await logEvent({
      layer: 'network',
      type: 'info',
      message: 'HTTP request sent',
      details: {
        headers: {
          'Content-Type': 'application/json',
          'CF-Ray': `${Math.random().toString(36).substr(2, 9)}-SJC`,
          'X-Real-IP': '192.168.1.1'
        },
        size: JSON.stringify(data).length + ' bytes'
      }
    });

    await logEvent({
      layer: 'cloudflare',
      type: 'info',
      message: 'Request received at edge',
      details: {
        datacenter: 'SJC (San Jose)',
        cache: 'MISS',
        firewall: 'PASSED'
      },
      duration: 30
    });

    await logEvent({
      layer: 'worker',
      type: 'info',
      message: 'Worker executing',
      details: {
        coldStart: false,
        memory: '128MB',
        cpu: '10ms limit'
      }
    });
  };

  const handleSignup = async () => {
    if (!state.email || !state.password) {
      await logEvent({
        layer: 'browser',
        type: 'error',
        message: 'Validation failed',
        details: { error: 'Email and password required' }
      });
      return;
    }

    setIsProcessing(true);
    setState(prev => ({ ...prev, stage: 'signup', events: [] }));

    try {
      // Browser validation
      await logEvent({
        layer: 'browser',
        type: 'info',
        message: 'Validating input',
        details: {
          email: state.email,
          passwordLength: state.password.length,
          passwordStrength: state.password.length > 8 ? 'strong' : 'weak'
        }
      });

      // Check email format
      await logEvent({
        layer: 'browser',
        type: 'info',
        message: 'Email validation',
        details: {
          format: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email) ? 'valid' : 'invalid',
          domain: state.email.split('@')[1]
        }
      });

      // Prepare request
      await simulateNetworkRequest('/api/auth/signup', {
        email: state.email,
        password: '[REDACTED]'
      });

      // Server-side processing
      await logEvent({
        layer: 'worker',
        type: 'info',
        message: 'Checking if user exists',
        details: { query: `SELECT * FROM users WHERE email = ?` }
      });

      await logEvent({
        layer: 'database',
        type: 'info',
        message: 'D1 query executed',
        details: {
          database: 'futr-bet-prod',
          table: 'users',
          result: 'No existing user',
          queryTime: '12ms'
        },
        duration: 120
      });

      // Hash password
      const hashedPassword = await hashPassword(state.password);

      // Create user
      await logEvent({
        layer: 'database',
        type: 'info',
        message: 'Creating user record',
        details: {
          query: 'INSERT INTO users (id, email, password_hash, created_at)',
          userId: `user-${Date.now()}`
        },
        duration: 80
      });

      // Generate tokens
      const userId = `user-${Date.now()}`;
      const token = await generateToken(userId);

      // Create session
      await logEvent({
        layer: 'database',
        type: 'info',
        message: 'Creating session',
        details: {
          table: 'sessions',
          sessionId: `sess-${Date.now()}`,
          expiresAt: new Date(Date.now() + 3600000).toISOString()
        }
      });

      // Send response
      await logEvent({
        layer: 'worker',
        type: 'success',
        message: 'Signup successful',
        details: {
          statusCode: 201,
          response: { userId, token: token.substring(0, 20) + '...' }
        }
      });

      await logEvent({
        layer: 'network',
        type: 'success',
        message: 'Response received',
        details: {
          statusCode: 201,
          headers: { 'Set-Cookie': 'auth-token=...; HttpOnly; Secure; SameSite=Strict' },
          size: '256 bytes',
          time: '342ms total'
        }
      });

      // Store in browser
      await logEvent({
        layer: 'browser',
        type: 'info',
        message: 'Storing auth token',
        details: {
          storage: 'localStorage',
          key: 'auth-token',
          expires: '1 hour'
        }
      });

      await logEvent({
        layer: 'browser',
        type: 'success',
        message: '🎉 SIGNUP COMPLETE!',
        details: {
          user: { id: userId, email: state.email },
          authenticated: true
        }
      });

      setState(prev => ({
        ...prev,
        stage: 'authenticated',
        token,
        user: { id: userId, email: state.email }
      }));

    } catch (error) {
      await logEvent({
        layer: 'browser',
        type: 'error',
        message: 'Signup failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
      setState(prev => ({ ...prev, stage: 'error' }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogin = async () => {
    setIsProcessing(true);
    setState(prev => ({ ...prev, stage: 'login', events: [] }));

    try {
      await simulateNetworkRequest('/api/auth/login', {
        email: state.email,
        password: '[REDACTED]'
      });

      // Find user
      await logEvent({
        layer: 'database',
        type: 'info',
        message: 'Finding user',
        details: {
          query: 'SELECT * FROM users WHERE email = ?',
          result: 'User found'
        },
        duration: 50
      });

      // Verify password
      await logEvent({
        layer: 'crypto',
        type: 'security',
        message: 'Comparing password hash',
        details: {
          method: 'bcrypt.compare',
          inputHash: 'hash(input)',
          storedHash: '$2b$10$...'
        },
        duration: 200
      });

      await logEvent({
        layer: 'crypto',
        type: 'success',
        message: 'Password verified',
        details: { match: true }
      });

      // Generate new session
      const token = await generateToken(`user-${Date.now()}`);

      await logEvent({
        layer: 'browser',
        type: 'success',
        message: '🎉 LOGIN SUCCESSFUL!',
        details: { authenticated: true }
      });

      setState(prev => ({
        ...prev,
        stage: 'authenticated',
        token,
        user: { email: state.email }
      }));

    } finally {
      setIsProcessing(false);
    }
  };

  const getEventIcon = (layer: string) => {
    const icons: Record<string, string> = {
      browser: '🌐',
      network: '📡',
      cloudflare: '☁️',
      worker: '⚙️',
      database: '🗄️',
      crypto: '🔐'
    };
    return icons[layer] || '📌';
  };

  const getEventColor = (type: string) => {
    const colors: Record<string, string> = {
      info: 'text-blue-400',
      success: 'text-green-400',
      error: 'text-red-400',
      security: 'text-yellow-400'
    };
    return colors[type] || 'text-gray-400';
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🎢 ULTRA AUTH EXPERIENCE</h1>
          <p className="text-gray-400">See EVERYTHING that happens when you authenticate</p>
          <p className="text-yellow-400 text-sm mt-2">The Space Mountain of web authentication - buckle up!</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Auth Form */}
          <div className="space-y-4">
            {/* Input Form */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">
                {state.stage === 'authenticated' ? '✅ AUTHENTICATED' : '🔐 AUTHENTICATION'}
              </h2>

              {state.stage !== 'authenticated' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm mb-1">Email Address</label>
                    <input
                      type="email"
                      value={state.email}
                      onChange={(e) => setState(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full bg-gray-900 border border-green-400 p-2 text-green-400"
                      placeholder="you@example.com"
                      disabled={isProcessing}
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={state.password}
                        onChange={(e) => setState(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full bg-gray-900 border border-green-400 p-2 text-green-400 pr-10"
                        placeholder="••••••••"
                        disabled={isProcessing}
                      />
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-2 text-gray-400 hover:text-green-400"
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleSignup}
                      disabled={isProcessing}
                      className="bg-green-400 text-black p-2 hover:bg-green-300 disabled:opacity-50"
                    >
                      {isProcessing && state.stage === 'signup' ? 'PROCESSING...' : 'SIGN UP'}
                    </button>
                    <button
                      onClick={handleLogin}
                      disabled={isProcessing}
                      className="bg-blue-400 text-black p-2 hover:bg-blue-300 disabled:opacity-50"
                    >
                      {isProcessing && state.stage === 'login' ? 'PROCESSING...' : 'LOG IN'}
                    </button>
                  </div>

                  <div className="mt-4 text-center">
                    <a
                      href="/password-reset"
                      className="text-xs text-gray-400 hover:text-green-400 underline"
                    >
                      Forgot your password? Reset it here →
                    </a>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-900 border border-green-400 p-4 rounded">
                    <h3 className="font-bold mb-2">Welcome, {state.user?.email}!</h3>
                    <p className="text-sm text-gray-300 mb-4">You're now authenticated with FutrBet</p>

                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="text-gray-400">User ID:</span>{' '}
                        <span className="font-mono">{state.user?.id}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Token:</span>{' '}
                        <span className="font-mono">{state.token?.substring(0, 30)}...</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Expires:</span>{' '}
                        <span className="font-mono">1 hour</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setState({
                      stage: 'idle',
                      email: '',
                      password: '',
                      token: null,
                      user: null,
                      events: []
                    })}
                    className="w-full bg-red-400 text-black p-2 hover:bg-red-300"
                  >
                    LOG OUT
                  </button>
                </div>
              )}
            </div>

            {/* What's Happening */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">📊 SYSTEM STATUS</h2>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Browser</span>
                  <span className={state.events.some(e => e.layer === 'browser') ? 'text-green-400' : 'text-gray-600'}>
                    {state.events.filter(e => e.layer === 'browser').length} events
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Network</span>
                  <span className={state.events.some(e => e.layer === 'network') ? 'text-green-400' : 'text-gray-600'}>
                    {state.events.filter(e => e.layer === 'network').length} events
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>CloudFlare</span>
                  <span className={state.events.some(e => e.layer === 'cloudflare') ? 'text-green-400' : 'text-gray-600'}>
                    {state.events.filter(e => e.layer === 'cloudflare').length} events
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Worker</span>
                  <span className={state.events.some(e => e.layer === 'worker') ? 'text-green-400' : 'text-gray-600'}>
                    {state.events.filter(e => e.layer === 'worker').length} events
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Database</span>
                  <span className={state.events.some(e => e.layer === 'database') ? 'text-green-400' : 'text-gray-600'}>
                    {state.events.filter(e => e.layer === 'database').length} events
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Crypto</span>
                  <span className={state.events.some(e => e.layer === 'crypto') ? 'text-green-400' : 'text-gray-600'}>
                    {state.events.filter(e => e.layer === 'crypto').length} events
                  </span>
                </div>
              </div>

              {state.events.length > 0 && (
                <div className="mt-4 pt-4 border-t border-green-400">
                  <div className="text-xs text-gray-400">
                    Total Time: {
                      state.events.length > 0
                        ? `${state.events[state.events.length - 1].timestamp - state.events[0].timestamp}ms`
                        : '0ms'
                    }
                  </div>
                  <div className="text-xs text-gray-400">
                    Total Events: {state.events.length}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Event Stream */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 LIVE EVENT STREAM</h2>

            <div className="h-[600px] overflow-y-auto space-y-2 text-xs">
              {state.events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Waiting for authentication events...
                </div>
              ) : (
                state.events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${
                      event.type === 'error' ? 'border-red-400' :
                      event.type === 'success' ? 'border-green-400' :
                      event.type === 'security' ? 'border-yellow-400' :
                      'border-blue-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getEventIcon(event.layer)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className={`font-bold ${getEventColor(event.type)}`}>
                            {event.layer.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-gray-300">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-1 text-xs">
                            {JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                        {event.duration && (
                          <div className="text-gray-600 mt-1">
                            Duration: {event.duration}ms
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: The Experience Summary */}
        {state.stage === 'authenticated' && (
          <div className="mt-6 border border-yellow-400 p-4">
            <h2 className="text-xl text-yellow-400 mb-2">🎢 THE RIDE IS COMPLETE!</h2>
            <p className="text-gray-400 mb-4">
              You just witnessed {state.events.length} events across 6 layers of the stack,
              from browser validation to cryptographic operations, through CloudFlare's edge network,
              into database queries, and back. This is what happens EVERY time someone logs in!
            </p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-green-400 font-bold">Security Operations:</span>
                <div className="text-gray-400">
                  • Password hashed with bcrypt<br />
                  • JWT token generated<br />
                  • Session created<br />
                  • HttpOnly cookie set
                </div>
              </div>
              <div>
                <span className="text-blue-400 font-bold">Network Journey:</span>
                <div className="text-gray-400">
                  • DNS resolution<br />
                  • TLS handshake<br />
                  • CloudFlare edge<br />
                  • Worker execution
                </div>
              </div>
              <div>
                <span className="text-yellow-400 font-bold">Data Operations:</span>
                <div className="text-gray-400">
                  • User lookup<br />
                  • Password verification<br />
                  • Session storage<br />
                  • Token generation
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}